import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useDrawerAskAIChatState } from '../contexts/DrawerAskAIContext'
import { useFloatingAskAISession } from '../hooks/useFloatingAskAISession'
import { useAskAIContextChip } from '../hooks/useAskAIContextChip'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import DrawerAskAIBubble from 'tripPlanner/components/DrawerAskAI/DrawerAskAIBubble'
import DrawerAskAIInput, { type DrawerAskAIInputHandle } from 'tripPlanner/components/DrawerAskAI/DrawerAskAIInput'
import ChatInputContextBar from '../components/ChatInputContextBar'
import { appendContextTag } from 'tripPlanner/utils/contextTag'
import zIndex from 'styles/tools/z-index'
import type { TripChatMobileTabId } from './TripChatMobileTabs'
import noop from 'lib/function/noop'

const FloatingRoot = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  z-index: ${zIndex.stickyFooter};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  padding-block-start: ${rem(12)};
  padding-block-end: calc(${rem(12)} + env(safe-area-inset-bottom, 0px));
  padding-inline: ${rem(16)};
  pointer-events: none;
  background: linear-gradient(
    to top,
    ${props => props.theme.palette.neutral.surface.normalBackground} 70%,
    transparent
  );

  > * {
    pointer-events: auto;
    width: 100%;
    max-width: ${rem(652)};
  }
`

const ComposerStack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &.has-context-bar {
    gap: 0;
  }
`

interface Props {
  /**
   * Whether this composer owns the Ask AI input for the current tab. It stays true while
   * an overlay covers the composer, so a context chip it is holding survives a drawer or
   * sheet opening over the top of it. The reply preview is deliberately not kept: it
   * restarts on `isVisible` so it can't adopt a turn another surface sent meanwhile.
   */
  isActive: boolean
  /** True while a mobile overlay covers the composer: it renders nothing but keeps its state. */
  isHidden?: boolean
  activeTab: TripChatMobileTabId
  /** Notifies the shell when the preview bubble needs extra List scroll inset. */
  onPreviewActiveChange?: (isPreviewActive: boolean) => void
}

function MobileFloatingAskAI({
  isActive,
  isHidden = false,
  activeTab,
  onPreviewActiveChange = noop,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBus()
  const inputRef = useRef<DrawerAskAIInputHandle>(null)
  const { submitMessage, messages, isStreaming } = useDrawerAskAIChatState()
  const isVisible = isActive && !isHidden

  const {
    handleSubmit: submitPlainMessage,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession,
  } = useFloatingAskAISession({
    submitMessage,
    messages,
    isStreaming,
    // Visibility, not ownership: the session pairs any user turn after its start index,
    // so a drawer's own Ask AI footer would otherwise hand this composer the drawer's
    // answer to preview when the drawer closes.
    isActive: isVisible,
  })

  const defaultPlaceholder = intl.formatMessage({
    id: 'ZiCPXGuGu8ee-O1qwe9E',
    defaultMessage: 'Ask me anything',
    description: 'Placeholder for the floating Ask AI input on the mobile List tab',
  })

  const handleOpenFullChat = useCallback(() => {
    eventBus.dispatch({ type: 'focus_chat' })
  }, [eventBus])

  const focusInput = useCallback(() => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [])

  const { askAIContext, clearAskAIContext } = useAskAIContextChip({
    enabled: isActive,
    onActivate: focusInput,
  })

  const handleSubmit = useCallback(async(message: string) => {
    if (askAIContext?.message) {
      const messageWithContext = appendContextTag(
        message,
        askAIContext.message,
        askAIContext.label,
      )
      clearAskAIContext()
      await submitPlainMessage(messageWithContext)
      return
    }
    await submitPlainMessage(message)
  }, [askAIContext, clearAskAIContext, submitPlainMessage])

  useEventSubscription('focus_floating_ask_ai', () => {
    if (!isVisible) return
    focusInput()
  })

  // A chip primed on List is stale after leaving that tab. Hiding behind an overlay leaves
  // activeTab alone, which is what keeps the chip.
  useEffect(() => {
    clearAskAIContext()
  }, [activeTab, clearAskAIContext])

  useEffect(() => {
    onPreviewActiveChange(isVisible && hasActiveSession)
    return () => {
      onPreviewActiveChange(false)
    }
  }, [hasActiveSession, isVisible, onPreviewActiveChange])

  // Convention exception: "Components should not determine whether they render".
  // The parent keeps this composer mounted while it is hidden: unmounting it would drop the
  // context chip it is holding every time a drawer, sheet or cart panel opens over the
  // List tab, and the event bus has no replay to recover the highlight either.
  if (!isVisible) return null

  return (
    <FloatingRoot data-testid="mobile-floating-ask-ai">
      {hasActiveSession && (
        <DrawerAskAIBubble
          latestResponse={latestResponse}
          sessionHistory={sessionHistory}
          isResponseStreaming={isResponseStreaming}
          isWaitingForResponse={isWaitingForResponse}
          onOpenFullChat={handleOpenFullChat}
          onSendMessage={handleSubmit}
        />
      )}
      <ComposerStack className={askAIContext ? 'has-context-bar' : undefined}>
        {askAIContext && (
          <ChatInputContextBar
            label={askAIContext.label}
            onClose={clearAskAIContext}
          />
        )}
        <DrawerAskAIInput
          ref={inputRef}
          onSubmit={handleSubmit}
          disabled={isStreaming}
          placeholder={askAIContext?.placeholder ?? defaultPlaceholder}
          hasActiveSession={hasActiveSession || !!askAIContext}
          forceExpanded
        />
      </ComposerStack>
    </FloatingRoot>
  )
}

MobileFloatingAskAI.displayName = 'MobileFloatingAskAI'

export default MobileFloatingAskAI
